import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import Header from '../Header';
import './index.scss';
import MetricCard from '../MetricCard';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { beeXLogger } from '../../../../helpers/logger';
import DisconnectionDuration from './DisconnectionDuration';
import EventFrequencyDisconnection from './EventFrequencyDisconnection';
import { ConnectionLog } from './ConnectionLog';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';
import Spinner from '../../../../components/Spinner';
import { Button, DatePicker } from 'antd';

export const CAMERA_STATE = {
  OFFLINE: 'Occluded',
  ONLINE: 'Defocused',
};

const CONNECTION_STATUS = 294;

const formatDurationString = (durationInput) => {
  let hours, minutes, seconds;

  if (typeof durationInput === 'number') {
    const totalSeconds = durationInput;
    hours = Math.floor(totalSeconds / 3600);
    minutes = Math.floor((totalSeconds % 3600) / 60);
    seconds = totalSeconds % 60;
  } else if (typeof durationInput === 'string') {
    const parts = durationInput.split(':');
    if (parts.length !== 3) return durationInput;

    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10);
    seconds = parseInt(parts[2], 10);
  } else {
    return '0s';
  }

  const durationParts = [];
  if (hours > 0) durationParts.push(`${hours}h`);
  if (minutes > 0) durationParts.push(`${minutes}m`);
  if (seconds > 0 || durationParts.length === 0)
    durationParts.push(`${seconds}s`);

  return durationParts.join(' ');
};

const ConnectionStatus = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  setExportDate,
}) => {
  const [metricsData, setMetricsData] = useState(null);
  const [metricsLoadingState, setMetricsLoadingState] = useState({});
  const [currentPageSize, setCurrentPageSize] = useState(3);
  const { namespace } = useSelector((state) => state);
  const { t } = useTranslation();
  const childReloadRefs = useRef({});
  const isMountedRef = useRef(true);
  const [selectedDate, setSelectedDate] = useState(moment());

  useEffect(() => {
    if (!isActive) {
      setCurrentPageSize(3);
    }
  }, [isActive]);

  const registerChildReload = useCallback((childKey, reloadFn) => {
    childReloadRefs.current[childKey] = reloadFn;
  }, []);

  const baseFetchData = useMonitoringDataFetch(CONNECTION_STATUS);
  const fetchConnectionStatusData = useCallback(async () => {
    if (!namespace) return null;

    try {
      const rawData = await baseFetchData();
      const transformedData = rawData
        .map((item) => ({
          ...item,
          startTime: item.startTime * 1000,
          endTime: item.endTime * 1000,
        }))
        .map((item) => {
          let type, label, value;
          type = 'offline';
          label = 'Disconnected Since';
          value = moment(Number(item.startTime) / 1000).fromNow();
          return {
            id: item.serial,
            title: item.name,
            serial: item.serial,
            status: 'offline',
            type,
            label,
            value,
            startTime: item.startTime,
            endTime: item.endTime,
          };
        });

      const loadingState = {};
      transformedData.forEach((metric) => {
        loadingState[metric.id] = true;
      });

      if (isMountedRef.current) {
        setMetricsLoadingState(loadingState);
        setTimeout(() => {
          if (isMountedRef.current) {
            const completedState = {};
            transformedData.forEach((metric) => {
              completedState[metric.id] = false;
            });
            setMetricsLoadingState(completedState);
          }
        }, 100);
      }

      setMetricsData(transformedData);
      return transformedData;
    } catch (err) {
      beeXLogger.offline(err);
      throw err;
    }
  }, [baseFetchData, namespace]);

  const { reload: mainReload } = useMonitoringDataWithInterval(
    fetchConnectionStatusData,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  const handleReload = useCallback(() => {
    mainReload();
    setMetricsData(null);
    Object.values(childReloadRefs.current).forEach((reloadFn) => {
      if (typeof reloadFn === 'function') {
        reloadFn();
      }
    });
  }, [mainReload]);

  useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(handleReload);
    }
  }, [handleReload, onRegisterReload]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <div className="connection-status-container">
      <Header
        title={t('System Continuity Monitoring')}
        subTitle={t(
          'Camera connectivity status overview and historical downtime trends.',
        )}
        chipLabel={t('Network active')}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBlock: 8,
        }}
      >
        <DatePicker
          className="picker"
          format="DD-MM-YYYY"
          placeholder={t('Select date')}
          value={selectedDate}
          style={{
            width: '255px',
            marginLeft: '20px',
            height: '32px',
          }}
          onChange={(value) => {
            setSelectedDate(value);
            if (setExportDate) {
              setExportDate(value);
            }
          }}
        />
      </div>
      <div className="connection-status-content">
        <div className="connection-status-metric-card-list">
          {metricsData
            ? metricsData
                .sort((a, b) => moment(b.startTime) - moment(a.startTime))
                .slice(0, currentPageSize)
                .map((metric) => (
                  <div
                    className={`connection-status-metric-card-item offline`}
                    key={metric.id}
                  >
                    {metricsLoadingState[metric.id] ? (
                      <Spinner />
                    ) : (
                      <MetricCard
                        header={
                          <ConnectionStatusCardHeader
                            warningType={metric.status}
                            title={metric.title}
                            serial={metric.serial}
                          />
                        }
                        body={null}
                        footer={
                          <ConnectionStatusCardFooter
                            label={metric.label}
                            value={metric.value}
                            type={metric.type}
                          />
                        }
                      />
                    )}
                  </div>
                ))
            : Array.from({ length: currentPageSize || 3 }).map((_, idx) => (
                <div
                  className="connection-status-metric-card-item"
                  key={`skeleton-${idx}`}
                >
                  <Spinner />
                </div>
              ))}
        </div>
        {metricsData?.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 16,
            }}
          >
            <Button
              type="primary"
              onClick={() =>
                setCurrentPageSize((prev) =>
                  metricsData?.length >= prev ? prev + 3 : prev,
                )
              }
            >
              {t('Load more')}
            </Button>
          </div>
        )}
        <div className="connection-status-section connection-status-section-flex">
          <div className="connection-status-section-flex-item">
            <EventFrequencyDisconnection
              isActive={isActive}
              intervalMs={intervalMs}
              onRegisterReload={(fn) =>
                registerChildReload('eventFrequency', fn)
              }
              selectedDate={selectedDate}
            />
          </div>
          <div className="connection-status-section-flex-item">
            <DisconnectionDuration
              isActive={isActive}
              intervalMs={intervalMs}
              onRegisterReload={(fn) =>
                registerChildReload('disconnection', fn)
              }
              selectedDate={selectedDate}
            />
          </div>
        </div>
        <div className="connection-status-section">
          <ConnectionLog
            isActive={isActive}
            intervalMs={intervalMs}
            onRegisterReload={(fn) => registerChildReload('connectionLog', fn)}
            selectedDate={selectedDate}
          />
        </div>
      </div>
    </div>
  );
};

export default ConnectionStatus;

export const ConnectionStatusCardHeader = ({ title = '', serial = '' }) => {
  return (
    <div className="connection-status-card-header">
      <div className="connection-status-card-header-left">
        <div className="connection-status-card-title">{title}</div>
        <div className="connection-status-card-serial">{serial}</div>
      </div>
      <div className="connection-status-card-header-right">
        <div
          className="connection-status-warning-type"
          style={{ backgroundColor: '#f52f39' }}
        >
          Offline
        </div>
      </div>
    </div>
  );
};

export const ConnectionStatusCardFooter = ({ label, value }) => {
  return (
    <div className="connection-status-card-footer">
      <span className="card-footer-label">{label}</span>
      <span className="card-footer-value" style={{ color: '#f52f39' }}>
        {value}
      </span>
    </div>
  );
};
